/**
 * useDeleteAccount Hook
 *
 * Owns all Delete Account page state + business logic: the adaptive 30-day
 * re-auth gate (whether to show login buttons), whether delete is allowed,
 * running the delete, and composing the shared provider-login flow so a fresh
 * sign-in recomputes the gate.
 *
 * Flutter parity: the confirm → delete → sign-out ordering mirrors
 * `AppSettingsScreen._onDeleteAccount` (lib/presentation/screens/settings/
 * app_settings_screen.dart:163-187) — the confirm sheet itself is fired by the
 * page (Task 6), and the CF-call-then-sign-out order lives in
 * `deleteAccountUseCase`. The adaptive login-button states are web-only (no
 * Flutter source), driven by `isReauthRequiredUseCase`.
 *
 * Layer ownership: like `useProviderLogin`, this hook owns state + Redux
 * `dispatch` but NEVER fires toasts / sheets / `Navigator` calls. It exposes
 * state + callbacks and forwards login-outcome reactions to page-provided
 * handlers; `confirmDelete` returns a boolean so the page chooses toast vs.
 * terminal state.
 */

import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/presentation/store';
import { logout } from '@/presentation/store/slices/auth/authSlice';
import { isReauthRequiredUseCase } from '@/domain/usecases/auth/isReauthRequiredUseCase';
import { deleteAccountUseCase } from '@/domain/usecases/user/deleteAccountUseCase';
import {
  useProviderLogin,
  type ProviderLoginHandlers,
} from '@/presentation/feature/login/hooks/useProviderLogin';
import { appLogger } from '@/domain/services/analytics/appLogger';

/**
 * The page's reactions to each recoverable login outcome. `onSuccess` is NOT
 * accepted here — the hook injects its own so a fresh sign-in recomputes the
 * staleness gate.
 */
export type DeleteAccountLoginHandlers = Pick<
  ProviderLoginHandlers,
  'onRemoving' | 'onSuspended' | 'onError'
>;

export interface UseDeleteAccountResult {
  /** Show login buttons: no established session OR the session is stale. */
  showLoginButtons: boolean;
  /** Delete is allowed: an established session with a fresh (non-stale) sign-in. */
  canDelete: boolean;
  /** True while the delete request + sign-out is in progress. */
  isDeleting: boolean;
  /** True once deletion has been successfully requested (terminal state). */
  isDeleted: boolean;
  /** True while a sign-in / setup flow is in progress. */
  isLoggingIn: boolean;
  onLoginWithGoogle: () => Promise<void>;
  onLoginWithApple: () => Promise<void>;
  /**
   * Runs the delete: guarded by `canDelete`, then CF-call → `logout`. Returns
   * `true` on success (page shows terminal state) / `false` on a no-op guard or
   * error (page shows a toast).
   */
  confirmDelete: () => Promise<boolean>;
}

export const useDeleteAccount = (
  loginHandlers?: DeleteAccountLoginHandlers
): UseDeleteAccountResult => {
  const dispatch = useDispatch<AppDispatch>();

  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const firebaseUserId = useSelector(
    (state: RootState) => state.auth.firebaseUserId
  );
  // Gate on a fully-established Expause session (the user entity), NOT raw
  // Firebase auth. `isAuthenticated` (== !!firebaseUserId) flips true on ANY
  // Firebase sign-in — including a `removing`/`suspended` login that
  // `completeLogin` blocks before ever dispatching `setUser` — so gating on it
  // would enable Delete for a user the removing/suspended guard just rejected.
  // A real session (fresh `completeLogin` → `setUser`, or restore via
  // `AppInitializer` / `AuthUserListener`) populates `state.auth.user`; a
  // blocked login leaves it `null`. `isAuthenticated` is retained only as an
  // effect trigger to recompute the staleness gate when identity changes.
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const hasSession = currentUser !== null;

  const [isReauthRequired, setIsReauthRequired] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  // Adaptive gate — exact inverses. When last-sign-in is not accessible,
  // `isReauthRequiredUseCase` returns false → treated as fresh → `canDelete`
  // true (once a full session exists).
  const showLoginButtons = !hasSession || isReauthRequired;
  const canDelete = hasSession && !isReauthRequired;

  // Recompute the 30-day staleness gate from Firebase Auth's lastSignInTime.
  const recomputeReauth = useCallback(() => {
    setIsReauthRequired(isReauthRequiredUseCase.execute());
  }, []);

  // Runs on mount and whenever auth identity changes.
  //
  // CRITICAL: this effect alone is NOT enough. A stale-but-authenticated
  // visitor who re-signs-in keeps `isAuthenticated === true` (Redux never
  // flips), so `[isAuthenticated, firebaseUserId]` does not change and the gate
  // would stay stale — hence `recomputeReauth` is ALSO injected as the login
  // hook's `onSuccess` below.
  useEffect(() => {
    recomputeReauth();
  }, [isAuthenticated, firebaseUserId, recomputeReauth]);

  // Login composition: forward the page's outcome handlers straight through,
  // and inject `onSuccess = recomputeReauth` so a fresh sign-in clears the
  // stale gate and enables delete.
  const providerLogin = useProviderLogin({
    ...loginHandlers,
    onSuccess: () => recomputeReauth(),
  });

  const confirmDelete = useCallback(async (): Promise<boolean> => {
    // Never call the delete CF without a valid, fresh session — the CF rejects
    // an unauthenticated caller.
    if (!canDelete) return false;

    setIsDeleting(true);
    try {
      await deleteAccountUseCase.execute();
      // Clear Redux + image cache AFTER `deleteAccountUseCase` already cleared
      // SecureStorage + Firebase (mirrors UserActionsContent.handleLogout's
      // signOut → dispatch(logout()) pairing).
      dispatch(logout());
      setIsDeleted(true);
      return true;
    } catch (e) {
      appLogger.error('Delete account failed', 'useDeleteAccount.confirmDelete', {
        error: e as Error,
      });
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [canDelete, dispatch]);

  return {
    showLoginButtons,
    canDelete,
    isDeleting,
    isDeleted,
    isLoggingIn: providerLogin.isLoggingIn,
    onLoginWithGoogle: providerLogin.loginWithGoogle,
    onLoginWithApple: providerLogin.loginWithApple,
    confirmDelete,
  };
};
